import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Building2, Bell, Send, ArrowRight, Lightbulb, Tag, MapPin, Users, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { slugify } from '../utils/slugify';
import { toast } from 'sonner';
import { suggestFromQuery } from '../utils/townSuggestions';

const API = '/api';

const SmartNoResults = ({ searchQuery, selectedTown, selectedCategory, onClearFilters, categories, categoryCounts }) => {
  const { user } = useAuth();
  const [relatedCats, setRelatedCats] = useState([]);
  const [relatedType, setRelatedType] = useState('popular');
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ business_name: '', category: '', town: selectedTown || '', contact_info: '', submitted_by_email: user?.email || '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alertSaved, setAlertSaved] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);

  const displayTerm = searchQuery || selectedCategory || 'this service';
  const capitalizedTerm = displayTerm.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

  // Check if user already has a saved search matching current query
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/saved-searches`).then(r => {
      const searches = r.data || [];
      const match = searches.find(s =>
        (s.keywords || '').toLowerCase() === (searchQuery || '').toLowerCase() &&
        (s.town || '').toLowerCase() === (selectedTown || '').toLowerCase() &&
        (s.category || '').toLowerCase() === (selectedCategory || '').toLowerCase()
      );
      if (match) setAlertSaved(true);
    }).catch(() => {});
  }, [searchQuery, selectedTown]);

  useEffect(() => {
    if (searchQuery || selectedCategory) {
      axios.get(`${API}/categories/related?q=${encodeURIComponent(searchQuery || selectedCategory)}`).then(r => {
        setRelatedCats(r.data.categories || []);
        setRelatedType(r.data.type || 'popular');
      }).catch(() => {});
    }
  }, [searchQuery, selectedCategory]);

  const handleSuggest = async () => {
    if (!suggestForm.business_name.trim()) { toast.error('Business name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/business-suggestions`, suggestForm);
      setSubmitted(true);
      toast.success('Thanks! Your suggestion has been received');
    } catch {
      toast.error('Failed to submit suggestion');
    }
    setSubmitting(false);
  };

  const saveSearchAlert = async () => {
    if (!user) { toast.error('Please log in to save search alerts'); return; }
    setSavingAlert(true);
    try {
      await axios.post(`${API}/saved-searches`, {
        keywords: searchQuery || '',
        town: selectedTown || '',
        category: selectedCategory || '',
        search_type: 'business',
        alerts_enabled: true,
      });
      setAlertSaved(true);
      toast.success('Search alert saved! We\'ll notify you when new listings match');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('You already have this search saved');
        setAlertSaved(true);
      } else {
        toast.error('Failed to save alert');
      }
    }
    setSavingAlert(false);
  };

  const townSuggestion = suggestFromQuery(searchQuery);

  return (
    <div className="space-y-6" data-testid="smart-no-results">
      {/* Did you mean? */}
      {townSuggestion && (
        <div className="p-4 bg-amber-50/80 border border-amber-200/60 rounded-xl flex items-center gap-3" data-testid="did-you-mean">
          <Search className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-slate-700">
            Did you mean{' '}
            <Link
              to={`/search?q=${encodeURIComponent(townSuggestion.fullQuery || townSuggestion.suggestion)}`}
              className="font-semibold text-[#0F2C3E] underline underline-offset-2 decoration-[#D4A017]/50 hover:decoration-[#D4A017]"
              data-testid="did-you-mean-link"
            >
              {townSuggestion.fullQuery || townSuggestion.suggestion}
            </Link>
            ?
          </p>
        </div>
      )}

      {/* Main CTA Card */}
      <Card className="border-[#D4A017]/20 bg-gradient-to-br from-white to-amber-50/40 overflow-hidden">
        <CardContent className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0F2C3E]/5 mb-4">
            <Search className="h-7 w-7 text-[#0F2C3E]/40" />
          </div>
          <h3 className="text-xl font-bold text-[#0F2C3E] mb-2" data-testid="no-results-heading">
            No listings found for "{displayTerm}"
            {selectedTown && <span className="text-slate-500 font-normal"> in {selectedTown}</span>}
          </h3>
          <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">
            Are you a {capitalizedTerm} or know one who should be listed on Discover Essex? Help us grow the directory!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/list-your-business">
              <Button className="bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] font-semibold gap-2" data-testid="no-results-list-business-btn">
                <Building2 className="h-4 w-4" /> List Your Business
              </Button>
            </Link>
            <Button
              variant="outline"
              className="gap-2 border-[#0F2C3E]/20 hover:border-[#0F2C3E]/40"
              onClick={() => setShowSuggestModal(true)}
              data-testid="suggest-business-btn"
            >
              <Lightbulb className="h-4 w-4 text-[#D4A017]" /> Suggest a Business
            </Button>
            <Button variant="ghost" onClick={onClearFilters} className="text-slate-400 text-sm">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Alert CTA */}
      {(searchQuery || selectedCategory) && user && !alertSaved && (
        <Card className="border-blue-100 bg-blue-50/40" data-testid="save-search-alert-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-full bg-blue-100 shrink-0">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">
                Get notified when a {capitalizedTerm}{selectedTown ? ` in ${selectedTown}` : ''} lists on Discover Essex
              </p>
              <p className="text-xs text-slate-400 mt-0.5">We'll email you when a new listing matches your search</p>
            </div>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white shrink-0 gap-1.5"
              onClick={saveSearchAlert}
              disabled={savingAlert}
              data-testid="save-search-alert-btn"
            >
              <Bell className="h-3.5 w-3.5" /> Notify Me
            </Button>
          </CardContent>
        </Card>
      )}
      {alertSaved && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-lg text-sm text-green-700" data-testid="alert-saved-confirmation">
          <CheckCircle className="h-4 w-4" /> Search alert saved — we'll notify you when new matches appear
        </div>
      )}

      {/* Related / Popular Categories */}
      {relatedCats.length > 0 && (
        <div data-testid="related-categories-section">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4 text-[#D4A017]" />
            {relatedType === 'related' ? 'Similar Categories' : 'Popular Categories'}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {relatedCats.map(cat => (
              <Link
                key={cat.category}
                to={`/business/${slugify(cat.category)}${selectedTown ? `?town=${encodeURIComponent(selectedTown)}` : ''}`}
                className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-lg hover:border-[#D4A017]/40 hover:bg-amber-50/30 transition-all group"
                data-testid={`related-cat-${cat.category}`}
              >
                <span className="text-sm text-slate-700 group-hover:text-[#0F2C3E] font-medium truncate">{cat.category}</span>
                <Badge variant="secondary" className="text-[10px] shrink-0 ml-2">{cat.count}</Badge>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Browse by popular categories from the page's categories */}
      {categories && categories.length > 0 && (
        <div data-testid="browse-categories-section">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-[#D4A017]" />
            Browse All Categories
          </h4>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter(cat => (categoryCounts?.[cat] || 0) > 0)
              .sort((a, b) => (categoryCounts?.[b] || 0) - (categoryCounts?.[a] || 0))
              .slice(0, 16)
              .map(cat => (
                <Link
                  key={cat}
                  to={`/business/${slugify(cat)}${selectedTown ? `?town=${encodeURIComponent(selectedTown)}` : ''}`}
                >
                  <Badge variant="outline" className="cursor-pointer hover:bg-[#D4A017]/10 hover:border-[#D4A017]/40 text-xs py-1.5 px-3">
                    {cat} <span className="ml-1 text-slate-400">{categoryCounts?.[cat] || 0}</span>
                  </Badge>
                </Link>
              ))}
          </div>
        </div>
      )}

      {/* Suggest Business Modal */}
      <Dialog open={showSuggestModal} onOpenChange={setShowSuggestModal}>
        <DialogContent className="sm:max-w-md" data-testid="suggest-business-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-[#D4A017]" />
              Suggest a Business
            </DialogTitle>
            <DialogDescription>
              Know a {capitalizedTerm}{selectedTown ? ` in ${selectedTown}` : ''} who should be listed? Let us know!
            </DialogDescription>
          </DialogHeader>
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="font-semibold text-slate-800 mb-1">Thanks for your suggestion!</h4>
              <p className="text-sm text-slate-500">We'll reach out to them about joining Discover Essex.</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 py-2">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Business Name *</label>
                  <Input
                    value={suggestForm.business_name}
                    onChange={e => setSuggestForm(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="e.g. DJ Danny Essex"
                    data-testid="suggest-business-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
                    <Input
                      value={suggestForm.category}
                      onChange={e => setSuggestForm(prev => ({ ...prev, category: e.target.value }))}
                      placeholder={searchQuery || selectedCategory || 'e.g. DJ'}
                      data-testid="suggest-business-category"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Town</label>
                    <Input
                      value={suggestForm.town}
                      onChange={e => setSuggestForm(prev => ({ ...prev, town: e.target.value }))}
                      placeholder={selectedTown || 'e.g. Chelmsford'}
                      data-testid="suggest-business-town"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Contact Info (optional)</label>
                  <Input
                    value={suggestForm.contact_info}
                    onChange={e => setSuggestForm(prev => ({ ...prev, contact_info: e.target.value }))}
                    placeholder="Website, phone, or social media link"
                    data-testid="suggest-business-contact"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Your Email (optional)</label>
                  <Input
                    value={suggestForm.submitted_by_email}
                    onChange={e => setSuggestForm(prev => ({ ...prev, submitted_by_email: e.target.value }))}
                    placeholder="your@email.com"
                    data-testid="suggest-business-email"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
                <Button
                  onClick={handleSuggest}
                  disabled={submitting}
                  className="bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] font-semibold gap-1.5"
                  data-testid="submit-suggestion-btn"
                >
                  <Send className="h-3.5 w-3.5" /> Submit Suggestion
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmartNoResults;
